/**
 * Team ▾ › Notes (2026-10-31: one entry instead of Comments, Suggestions, Leave a note… and Handoff notes): one dialog
 * with the four as tabs, each the list or form the dialogs of their own show (ProjectCommentsBody, SuggestionsList,
 * HandoffForm, HandoffNotesList).
 *
 * - `tab` opens it on a section (NOTES_TABS: ⌘K's "Suggestions", a suggestion in Activity, Home's Reviews gadget);
 *   `focusId` the suggestion or the note to show first.
 * - Suggestions carries the count waiting for this person (`suggestionsPending` until its list loads, then the list's).
 * - Leave a note, once sent, lands on Handoff notes, the new note on top; Leave a note in Handoff notes goes the other way.
 * - A fixed width (880px, never wider than the screen less 32px) and height, the tab bar fixed and the section scrolling
 *   inside; a phone gets the whole screen.
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Tabs } from 'antd';
import { usePhone } from '../../hooks/useMediaQuery';
import { ProjectCommentsBody } from './ProjectComments';
import { SuggestionsList } from './Suggestions';
import { HandoffForm, HandoffNotesList } from './Handoff';
import './Collab.css';

/** The sections, in the order of the tabs. */
export const NOTES_TABS = [
  { id: 'comments', label: 'Comments', desc: 'Every comment thread on the project\'s files.' },
  { id: 'suggestions', label: 'Suggestions', desc: 'Changes to files proposed with Suggest a change; editors accept or decline them.' },
  { id: 'leave', label: 'Leave a note', desc: 'A message to people on this project; they get a notification.' },
  { id: 'handoff', label: 'Handoff notes', desc: 'What was done and what is next, the notes sent and received.' },
];

/** What Notes is, in one line (Team ▾, the phone's Menu and ⌘K say the same). */
export const NOTES_DESC = 'Comments, suggestions and notes for the people on this project.';

const tabOf = (id) => (NOTES_TABS.some((tab) => tab.id === id) ? id : 'comments');

export default function NotesModal({
  projectId, open, onClose, tab = 'comments', focusId = null, suggestionsPending = 0, onOpenFile = null, projectName = '', since = null,
}) {
  const phone = usePhone();
  const [active, setActive] = useState(tabOf(tab));
  const [waiting, setWaiting] = useState(suggestionsPending);
  const [busy, setBusy] = useState(false);
  // A new form after a send; the notes list reloads (it may be mounted already, from an earlier visit).
  const [sentCount, setSentCount] = useState(0);
  useEffect(() => { if (open) { setActive(tabOf(tab)); } }, [open, tab, focusId]);
  useEffect(() => { setWaiting(suggestionsPending); }, [suggestionsPending]);
  const close = useCallback(() => { if (!busy) { onClose(); } }, [busy, onClose]);
  const sent = useCallback(() => { setSentCount((n) => n + 1); setActive('handoff'); }, []);

  const label = (section) => (
    <span className="collab-notes-tab">
      {section.label}
      {section.id === 'suggestions' && waiting > 0 && <span aria-label={`${waiting} waiting`} className="collab-notes-count">{waiting > 99 ? '99+' : waiting}</span>}
    </span>
  );
  const body = {
    comments: <ProjectCommentsBody onClose={onClose} onOpenFile={onOpenFile} projectId={projectId} />,
    suggestions: <SuggestionsList focusId={active === 'suggestions' ? focusId : null} onClose={onClose} onWaiting={setWaiting} projectId={projectId} />,
    leave: (
      <HandoffForm
        actionsClassName="collab-notes-actions"
        key={`leave-${sentCount}`}
        onBusy={setBusy}
        onCancel={onClose}
        onDone={sent}
        projectId={projectId}
        projectName={projectName}
        since={since}
      />
    ),
    handoff: <HandoffNotesList focusId={active === 'handoff' ? focusId : null} key={`notes-${sentCount}`} onLeaveNote={() => setActive('leave')} projectId={projectId} />,
  };
  return (
    <Modal
      centered={!phone}
      className={`collab-notes-modal${phone ? ' is-phone' : ''}`}
      destroyOnHidden
      footer={null}
      keyboard={!busy}
      mask={{ closable: !busy }}
      onCancel={close}
      open={open}
      title="Notes"
      width={phone ? '100vw' : 'min(880px, calc(100vw - 32px))'}
    >
      <div className="collab-notes" data-testid="notes-modal">
        <Tabs
          activeKey={active}
          className="collab-notes-tabs"
          items={NOTES_TABS.map((section) => ({
            key: section.id,
            label: label(section),
            children: <div className="collab-notes-pane" data-testid={`notes-pane-${section.id}`}>{body[section.id]}</div>,
          }))}
          onChange={setActive}
        />
      </div>
    </Modal>
  );
}

NotesModal.propTypes = {
  focusId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  // ({ path, line }) => void: a comment's file at its line in Code.
  onOpenFile: PropTypes.func,
  open: PropTypes.bool,
  projectId: PropTypes.string,
  projectName: PropTypes.string,
  since: PropTypes.string,
  suggestionsPending: PropTypes.number,
  tab: PropTypes.oneOf(NOTES_TABS.map((section) => section.id)),
};
